import { db } from '../../db';
import { addDoc, collection, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { normalizeLicenseDateFields } from '../licenseDates';

export class FirestoreLicenseRepository {
  async createLicense(cityId, payload = {}) {
    const data = normalizeLicenseDateFields(payload?.data || {});
    const firestore = await db;
    if (data.id) {
      await this.saveLicense(cityId, data.id, data);
      return {
        license: {
          id: data.id,
          data
        }
      };
    }
    const created = await addDoc(collection(firestore, 'cities', cityId, 'licenses'), data);
    return {
      license: {
        id: created.id,
        data
      }
    };
  }

  async updateLicense(cityId, licenseId, payload = {}) {
    const data = normalizeLicenseDateFields(payload?.data || {});
    await this.saveLicense(cityId, licenseId, data);
    return {
      license: {
        id: licenseId,
        data
      }
    };
  }

  async updateLicenseProperty(cityId, licenseId, payload = {}) {
    const hasPropertyId = Object.prototype.hasOwnProperty.call(payload || {}, 'propertyId');
    if (!hasPropertyId) {
      throw new Error('propertyId is required');
    }

    const normalizedPropertyId = typeof payload.propertyId === 'string'
      ? payload.propertyId.trim() || null
      : (payload.propertyId || null);

    const patch = normalizedPropertyId
      ? { propertyId: normalizedPropertyId }
      : { propertyId: null, property: null };
    await this.saveLicense(cityId, licenseId, patch);
    return {
      license: {
        id: licenseId,
        data: patch
      }
    };
  }

  async updateLicenseContact(cityId, licenseId, payload = {}) {
    const contact = {
      owners: Array.isArray(payload.names)
        ? payload.names.map(name => String(name || '').trim()).filter(Boolean)
        : [],
      address: payload.address === undefined ? null : (payload.address || null),
      email: payload.email === undefined ? null : (payload.email || null),
      phone: payload.phone === undefined ? null : (payload.phone || null)
    };

    const patch = { contact };
    await this.saveLicense(cityId, licenseId, patch);
    return {
      license: {
        id: licenseId,
        data: patch
      }
    };
  }

  async updateLicenseStatusAndDates(cityId, licenseId, payload = {}) {
    const patch = {};
    const hasStatus = Object.prototype.hasOwnProperty.call(payload || {}, 'status');
    if (hasStatus) {
      patch.status = payload.status ?? null;
    }

    const hasAppliedOn = Object.prototype.hasOwnProperty.call(payload || {}, 'appliedOn')
      || Object.prototype.hasOwnProperty.call(payload || {}, 'dateApplied');
    if (hasAppliedOn) {
      patch.dateApplied = (Object.hasOwn(payload, 'appliedOn') ? payload.appliedOn : payload.dateApplied) ?? null;
    }

    const hasIssuedOn = Object.prototype.hasOwnProperty.call(payload || {}, 'issuedOn')
      || Object.prototype.hasOwnProperty.call(payload || {}, 'dateIssued');
    if (hasIssuedOn) {
      patch.issuedOn = (Object.hasOwn(payload, 'issuedOn') ? payload.issuedOn : payload.dateIssued) ?? null;
    }

    await this.saveLicense(cityId, licenseId, patch);
    return {
      license: {
        id: licenseId,
        data: patch
      }
    };
  }

  async saveLicense(cityId, licenseId, data) {
    const firestore = await db;
    return setDoc(doc(firestore, 'cities', cityId, 'licenses', licenseId), normalizeLicenseDateFields(data), { merge: true });
  }

  async deleteLicense(cityId, licenseId) {
    const firestore = await db;
    return deleteDoc(doc(firestore, 'cities', cityId, 'licenses', licenseId));
  }

  async updateLicenseApplication(cityId, applicationId, patch) {
    const firestore = await db;
    return setDoc(doc(firestore, 'cities', cityId, 'licenseApplications', applicationId), patch, { merge: true });
  }

  async addReport(cityId, data) {
    const firestore = await db;
    return addDoc(collection(firestore, 'cities', cityId, 'reports'), data);
  }

  async importLicenses() {
    throw new Error('importLicenses is not supported by FirestoreLicenseRepository');
  }
}
